import type { ShellConfig } from '../portal/shellTypes';

// =============================================================================
// Studio preview protocol
// =============================================================================
//
// The site-builder admin embeds a portal's /studio-preview/page and
// /studio-preview/shell routes in iframes (or opens them in a tab) and drives
// them over postMessage:
//
//   portal → admin:  { kind: 'preview-ready' }
//   admin → portal:  { kind: 'config-update', config?, locale?, themeMode? }
//
// `config` is the SiteShell block's props and only the shell preview reads it;
// the page preview takes its content from the URL and reloads for anything
// but a theme-mode change. Every portal host serves these routes, so the
// protocol lives here rather than in each host.

export type PreviewThemeMode = 'light' | 'dark';

export interface PreviewConfigUpdate {
  kind: 'config-update';
  config?: ShellConfig;
  locale?: string;
  themeMode?: PreviewThemeMode;
}

/** The admin dev servers' origins, trusted when a host configures no list. */
export const DEV_PREVIEW_ORIGINS: readonly string[] = [
  'http://localhost:5173',
  'http://localhost:5174',
  'http://127.0.0.1:5173',
  'http://127.0.0.1:5174',
];

/**
 * The origins a preview accepts messages from.
 *
 * <p>{@code listed} is the host's configured list — an array, or the
 * comma-separated {@code NEXT_PUBLIC_PREVIEW_ALLOWED_ORIGINS} form. Empty
 * means {@link DEV_PREVIEW_ORIGINS}. The preview's own origin is always
 * trusted: an admin served from the portal's host (the LMS's /page/90 mount)
 * must not be dropped by a list that names only another environment.
 */
export function previewAllowedOrigins(
  listed: string | readonly string[] | null | undefined,
  self: string | null | undefined,
): string[] {
  const entries = typeof listed === 'string' ? listed.split(',') : (listed ?? []);
  const cleaned = entries.map((origin) => origin.trim()).filter(Boolean);
  const origins = cleaned.length > 0 ? cleaned : [...DEV_PREVIEW_ORIGINS];
  return self && !origins.includes(self) ? [...origins, self] : origins;
}

/**
 * The config update a message carries, or null when it is not one or comes
 * from an origin not in {@code allowedOrigins}. Fields of the wrong type are
 * dropped rather than trusted.
 */
export function readPreviewConfigUpdate(
  event: Pick<MessageEvent, 'origin' | 'data'>,
  allowedOrigins: readonly string[],
): PreviewConfigUpdate | null {
  if (!allowedOrigins.includes(event.origin)) return null;
  const data = event.data as Record<string, unknown> | null;
  if (!data || typeof data !== 'object' || data.kind !== 'config-update') return null;
  const update: PreviewConfigUpdate = { kind: 'config-update' };
  if (data.config && typeof data.config === 'object') update.config = data.config as ShellConfig;
  if (typeof data.locale === 'string' && data.locale) update.locale = data.locale;
  if (data.themeMode === 'light' || data.themeMode === 'dark') update.themeMode = data.themeMode;
  return update;
}

/**
 * Show the preview in a theme mode the way a visitor's own choice does:
 * {@code data-theme} on the document, plus the saved preference and change
 * event {@code ThemeToggle} reads, so a toggle in the previewed header shows
 * the same mode.
 */
export function applyPreviewThemeMode(mode: PreviewThemeMode): void {
  try {
    document.documentElement.setAttribute('data-theme', mode);
    window.localStorage.setItem('lms-theme', mode);
    window.dispatchEvent(new Event('lms-theme-change'));
  } catch {
    /* storage blocked in a sandboxed frame */
  }
}

/**
 * Tell the admin the preview is listening: its iframe's parent, or the tab
 * that opened it. Posted to any origin because the preview does not yet know
 * who embedded it and the message carries nothing; inbound messages are what
 * the allow-list guards.
 */
export function announcePreviewReady(): void {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ kind: 'preview-ready' }, '*');
  } else if (window.opener) {
    (window.opener as Window).postMessage({ kind: 'preview-ready' }, '*');
  }
}
